import React from "react";

const HVACRoles = () => {
  return (
    <div className="min-h-screen bg-black text-gray-200 px-4 py-6 md:px-10">
      
      {/* Header */}
      <header className="mb-8">
        <h1 className="text-xl lg:text-2xl font-bold text-yellow-200 flex items-center gap-3">
          📋 Complete HVAC Role Taxonomy
        </h1>
        <p className="mt-3 text-sm lg:text-sm text-gray-400 leading-relaxed">
          Full spectrum of HVAC roles — from design, manufacturing, and installation to smart, digital, compliance, and future-ready positions.
        </p>
      </header>

      {/* Section Template */}
      <section className="mb-2">
        <h2 className="text-lg lg:text-xl font-semibold text-cyan-400 flex items-center gap-2 mb-2">
          1. DESIGN, ENGINEERING & CONSULTING ROLES
        </h2>
        <ul className="list-disc ml-5 text-sm [&>li]:text-white">
          <li>HVAC Design Engineer</li>
          <li>HVAC System Design Engineer</li>
          <li>Mechanical Design Engineer (HVAC)</li>
          <li>VRF System Design Engineer</li>
          <li>Chiller Plant Design Engineer</li>
          <li>Heat Pump Design Engineer</li>
          <li>District Cooling Engineer</li>
          <li>Refrigeration Design Engineer</li>
          <li>Energy Modeling Engineer</li>
          <li>CFD (Computational Fluid Dynamics) Engineer</li>
          <li>Duct Design Engineer</li>
          <li>Piping Design Engineer</li>
          <li>Plumbing & HVAC Engineer (MEP)</li>
          <li>HVAC Consultant</li>
          <li>MEP Consultant</li>
          <li>Sustainability / Green Building Consultant</li>
          <li>Energy Efficiency Consultant</li>
          <li>IAQ (Indoor Air Quality) Specialist</li>
          <li>Net-Zero / Carbon Consultant</li>
        </ul>
      </section>

      {/* Section 2 */}
      <section className="mb-2">
        <h2 className="text-lg lg:text-xl font-semibold text-cyan-400 flex items-center gap-2 mb-2">
          2. OEM, MANUFACTURING & R&D ROLES
        </h2>
        <ul className="list-disc ml-5 text-sm [&>li]:text-white">
          <li>HVAC Product Engineer</li>
          <li>HVAC R&D Engineer</li>
          <li>Compressor Design Engineer</li>
          <li>Heat Exchanger Engineer</li>
          <li>Power Electronics Engineer (Inverter Drives)</li>
          <li>Embedded Systems Engineer (HVAC Controls)</li>
          <li>Refrigerant Technology Specialist</li>
          <li>PCB Design Engineer (HVAC Electronics)</li>
          <li>HVAC Production Engineer</li>
          <li>Assembly Line Supervisor</li>
          <li>Quality Assurance Engineer (HVAC)</li>
          <li>Supplier Quality Engineer</li>
          <li>Lean / Six Sigma Engineer (HVAC)</li>
        </ul>
      </section>

      {/* Section 3 */}
      <section className="mb-2">
        <h2 className="text-lg lg:text-xl font-semibold text-cyan-400 flex items-center gap-2 mb-2">
          3. INSTALLATION, COMMISSIONING & PROJECT EXECUTION
        </h2>
        <ul className="list-disc ml-5 text-sm [&>li]:text-white">
          <li>HVAC Project Manager</li>
          <li>HVAC Site Engineer</li>
          <li>HVAC Supervisor / Foreman</li>
          <li>HVAC Installation Engineer</li>
          <li>Commissioning Engineer (CxA)</li>
          <li>Testing, Adjusting & Balancing (TAB) Engineer</li>
          <li>HVAC Technician</li>
          <li>HVAC Installer / Fitter</li>
          <li>Refrigeration Mechanic</li>
          <li>VRF Technician</li>
          <li>Chiller Technician</li>
          <li>AHU Technician</li>
          <li>Duct Fabrication Technician</li>
          <li>Piping Technician</li>
          <li>Copper Brazing Technician</li>
          <li>Insulation Technician</li>
          <li>Electrician (HVAC)</li>
          <li>Controls / BMS Technician</li>
          <li>HVAC Helper</li>
          <li>HVAC Apprentice</li>
          <li>Refrigeration Assistant</li>
        </ul>
      </section>

      {/* Section 4 */}
      <section className="mb-2">
        <h2 className="text-lg lg:text-xl font-semibold text-cyan-400 flex items-center gap-2 mb-2">
          4. SERVICE, MAINTENANCE & OPERATIONS
        </h2>
        <ul className="list-disc ml-5 text-sm [&>li]:text-white">
          <li>HVAC Service Engineer</li>
          <li>HVAC Maintenance Engineer</li>
          <li>Preventive Maintenance Technician</li>
          <li>Breakdown Service Technician</li>
          <li>Field Service Technician</li>
          <li>Facility Manager (HVAC Focus)</li>
          <li>Building Operations Engineer</li>
          <li>Plant Room Operator</li>
          <li>Utility Operator (Chiller / Boiler)</li>
        </ul>
      </section>

      {/* Section 5 */}
      <section className="mb-2">
        <h2 className="text-lg lg:text-xl font-semibold text-cyan-400 flex items-center gap-2 mb-2">
          5. DIGITAL, SMART HVAC & IIoT ROLES
        </h2>
        <ul className="list-disc ml-5 text-sm [&>li]:text-white">
          <li>Smart HVAC Engineer</li>
          <li>HVAC Controls Engineer</li>
          <li>BMS Engineer</li>
          <li>SCADA Engineer (HVAC Systems)</li>
          <li>IIoT Engineer (HVAC)</li>
          <li>Digital Twin Engineer (Buildings/HVAC)</li>
          <li>HVAC Data Analyst</li>
          <li>Energy Analytics Engineer</li>
          <li>Predictive Maintenance Engineer</li>
          <li>AI/ML Engineer (HVAC Optimization)</li>
          <li>OT Cybersecurity Engineer (HVAC/BMS)</li>
          <li>Building Systems IT Administrator</li>
        </ul>
      </section>

      {/* Section 6 */}
      <section className="mb-2">
        <h2 className="text-lg lg:text-xl font-semibold text-cyan-400 flex items-center gap-2 mb-2">
          6. ENERGY, SUSTAINABILITY & COMPLIANCE
        </h2>
        <ul className="list-disc ml-5 text-sm [&>li]:text-white">
          <li>Energy Manager</li>
          <li>Energy Auditor</li>
          <li>Refrigerant Compliance Officer</li>
          <li>A2L Refrigerant Safety Specialist</li>
          <li>Environmental Compliance Manager</li>
          <li>ESG / Sustainability Reporting Specialist</li>
        </ul>
      </section>

      {/* Section 7 */}
      <section className="mb-2">
        <h2 className="text-lg lg:text-xl font-semibold text-cyan-400 flex items-center gap-2 mb-2">
          7. SALES, BUSINESS & CUSTOMER INTERFACE
        </h2>
        <ul className="list-disc ml-5 text-sm [&>li]:text-white">
          <li>HVAC Sales Engineer</li>
          <li>Application Engineer</li>
          <li>Technical Pre-Sales Engineer</li>
          <li>Channel Sales Manager</li>
          <li>Key Account Manager</li>
          <li>HVAC Customer Support Engineer</li>
          <li>Service Coordinator</li>
          <li>AMC / Contract Manager</li>
        </ul>
      </section>

      {/* Section 8 */}
      <section className="mb-2">
        <h2 className="text-lg lg:text-xl font-semibold text-cyan-400 flex items-center gap-2 mb-2">
          8. MSME, CONTRACTING & ENTREPRENEURIAL ROLES
        </h2>
        <ul className="list-disc ml-5 text-sm [&>li]:text-white">
          <li>HVAC Contractor</li>
          <li>HVAC EPC Contractor</li>
          <li>HVAC MSME Owner</li>
          <li>Service Franchise Owner</li>
          <li>Local Service Partner</li>
          <li>OEM Authorized Service Partner</li>
        </ul>
      </section>

      {/* Section 9 */}
      <section className="mb-2">
        <h2 className="text-lg lg:text-xl font-semibold text-cyan-400 flex items-center gap-2 mb-2">
          9. SAFETY, QUALITY & RISK MANAGEMENT
        </h2>
        <ul className="list-disc ml-5 text-sm [&>li]:text-white">
          <li>HVAC Safety Officer</li>
          <li>Site Safety Supervisor</li>
          <li>QA/QC Engineer (HVAC Projects)</li>
          <li>Risk & Compliance Manager</li>
        </ul>
      </section>

      {/* Section 10 */}
      <section className="mb-2">
        <h2 className="text-lg lg:text-xl font-semibold text-cyan-400 flex items-center gap-2 mb-2">
          10. TRAINING, L&D & ECOSYSTEM ROLES
        </h2>
        <ul className="list-disc ml-5 text-sm [&>li]:text-white">
          <li>HVAC Trainer / Instructor</li>
          <li>Master Technician</li>
          <li>Industry Assessor</li>
          <li>Certification Program Manager</li>
          <li>Skill Development Officer</li>
          <li>Apprenticeship Program Lead</li>
        </ul>
      </section>

      {/* Section 11 */}
      <section className="mb-2">
        <h2 className="text-lg lg:text-xl font-semibold text-cyan-400 flex items-center gap-2 mb-2">
          11. POLICY, REGULATORY & INDUSTRY ROLES
        </h2>
        <ul className="list-disc ml-5 text-sm [&>li]:text-white">
          <li>HVAC Policy Advisor</li>
          <li>Standards Committee Member (ASHRAE, BIS)</li>
          <li>Government HVAC Program Officer</li>
          <li>PLI / Manufacturing Scheme Consultant</li>
        </ul>
      </section>

      {/* Section 12 */}
      <section className="mb-10">
        <h2 className="text-lg lg:text-xl font-semibold text-cyan-400 flex items-center gap-2 mb-2">
          12. EMERGING & FUTURE HVAC ROLES (2026+)
        </h2>
        <ul className="list-disc ml-5 text-sm [&>li]:text-white">
          <li>Climate Infrastructure Engineer</li>
          <li>Net-Zero HVAC Architect</li>
          <li>HVAC Retrofit Specialist</li>
          <li>District Cooling Planner</li>
          <li>Hydrogen / Alternative Refrigerant Specialist</li>
          <li>Carbon Accounting Engineer (Buildings)</li>
        </ul>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-800 pt-4">
        <p className="text-xs text-white text-center">
          Map roles → skills → certifications → jobs. Attach Think5Whys learning paths per role. HVAC is no longer a trade — HVAC is climate infrastructure engineering. Think5Whys + Agile + Micro Lean Sigma + IoT stack is role-ready for this shift.
        </p>
      </footer>
    </div>
  );
};

export default HVACRoles;
